import { isTuple } from '../core/packet';
import type { PacketMaterial } from '../core/packet';
import type { MaterialId } from './materials';

export type RecipeId = 'makeGlass' | 'makeSilicon' | 'makeSteel';

/**
 * Every recipe `map` can be configured with. `from` is a list because a recipe's callback takes
 * either a lone packet or the tuple `zip` packed — one operator, two arities, since `map` is the
 * only thing in the game that transforms anything at all.
 */
export interface RecipeDef {
  id: RecipeId;
  label: string;
  from: readonly MaterialId[];
  to: MaterialId;
  cost: number;
}

export const RECIPES: Record<RecipeId, RecipeDef> = {
  makeGlass: { id: 'makeGlass', label: 'Make Glass', from: ['sand'], to: 'glass', cost: 60 },
  makeSilicon: {
    id: 'makeSilicon',
    label: 'Make Silicon',
    from: ['sand'],
    to: 'silicon',
    cost: 70,
  },
  makeSteel: {
    id: 'makeSteel',
    label: 'Make Steel',
    from: ['iron', 'carbon'],
    to: 'steel',
    cost: 90,
  },
};

export type PlannedRecipeId = 'makeMotor' | 'makeSolarPanel' | 'makeRover';

/**
 * The rest of the tree as ROADMAP designs it. Deliberately separate from {@link RECIPES}: no handler
 * runs these and no operator can be configured with one, so anything the sim reaches for must never
 * find them. The recipe book draws them so a player can see where the tree is going rather than
 * guessing that the game simply stops at steel.
 */
export interface PlannedRecipeDef {
  id: PlannedRecipeId;
  label: string;
  from: readonly MaterialId[];
  to: MaterialId;
  reason: string;
}

export const PLANNED_RECIPES: readonly PlannedRecipeDef[] = [
  {
    id: 'makeMotor',
    label: 'Make Motor',
    from: ['steel', 'copper'],
    to: 'motor',
    reason: 'Waiting on a handler — the Copper Mine arrives with the Copper Flats expansion',
  },
  {
    id: 'makeSolarPanel',
    label: 'Make Solar Panel',
    from: ['glass', 'silicon', 'copper'],
    to: 'solarPanel',
    reason: 'Waiting on a three-input join',
  },
  {
    id: 'makeRover',
    label: 'Make Rover',
    from: ['motor', 'solarPanel'],
    to: 'rover',
    reason: 'Waiting on Motor and Solar Panel',
  },
];

/** A recipe whose callback destructures a tuple — the only kind that can consume what `zip` packs. */
export function isTupleRecipe(recipe: RecipeDef): boolean {
  return recipe.from.length > 1;
}

/**
 * Which physical side each half of a tuple arrived on is not content — a join's input ports are
 * ordered by an invisible compass sweep — so a tuple is matched as an unordered multiset.
 */
export function recipeAccepts(recipe: RecipeDef, material: PacketMaterial): boolean {
  if (!isTuple(material)) return !isTupleRecipe(recipe) && recipe.from[0] === material;
  if (material.length !== recipe.from.length) return false;
  const remaining = [...recipe.from];
  return material.every((part) => {
    const index = remaining.indexOf(part);
    if (index === -1) return false;
    remaining.splice(index, 1);
    return true;
  });
}
